// Some apps ship Desk-wide rules inside their own stylesheet. That stylesheet loads after the theme's
// (apps load in install order), and its unscoped rules restyle every screen, not only the app's own.
// The theme contains them: when such a stylesheet is on the page, the rules listed here are deleted
// from it, and the custom properties listed are dropped. This runs before the Desk draws: a classic
// script waits for the stylesheets above it, and the Desk renders on document ready. Everything else
// in the app's stylesheet stays, so its own screens look as the app intends.
//
// Rules are matched by their exact media and selector text. When a new release of the app changes a
// rule, it is no longer found: the console says which, and the rule is left in place, not guessed at.
// An entry can also name parts of the app's Desk script to skip (see contain_script below), with the
// same rule: what is not found is reported and left alone.

export const CONTAIN = [
	{
		// advanced_compliance 1.1.5 (version-15, d4d5807), installed unchanged on urban (owner, 19 Oct 2026).
		app: "advanced_compliance",
		sheet: "/assets/advanced_compliance/css/advanced_compliance.css",
		rules: [
			// phones: every control and every button 44px tall, and every button 10px 16px
			{ media: "(max-width: 768px)", selector: ".frappe-control" },
			{ media: "(max-width: 768px)", selector: ".btn" },
			// every form's activity timeline: 24px more under each item, and a blue dot beside it
			{ selector: ".timeline-item" },
			{ selector: ".timeline-item::before" },
			{ selector: ".timeline-item.urgent::before" },
		],
		properties: [
			// every card's shadow, light and dark (the platform's own is var(--shadow-sm))
			{ selector: ":root", property: "--card-shadow" },
			{ selector: '[data-theme="dark"]', property: "--card-shadow" },
		],
		// The app's Desk script, as UDF1 decided under the owner's hands-off go (19 Oct 2026): skip its
		// inline 44px height on the controls present at load (touch devices and phones), and of its five
		// Alt shortcuts leave out only Alt+D, which takes the browser's address-bar key. Alt+C/R/T/Q stay.
		script: {
			object: "advanced_compliance",
			noop: ["setup_mobile_handlers"],
			skip_shortcuts: { setup_keyboard_shortcuts: ["alt+d"] },
		},
	},
];

// The app defines its object when its script runs, after this bundle, and calls init() on document
// ready. jQuery runs ready handlers in the order they were added, and this one is added first, so the
// object exists and has not run yet when it is patched here.
export function contain_script(entry, root = window) {
	const spec = entry.script;
	const obj = spec && root[spec.object];
	if (!obj) return null;
	const missing = [];
	for (const name of spec.noop || []) {
		if (typeof obj[name] === "function") obj[name] = function () {};
		else missing.push(name);
	}
	for (const [name, keys] of Object.entries(spec.skip_shortcuts || {})) {
		const original = obj[name];
		if (typeof original !== "function") {
			missing.push(name);
			continue;
		}
		obj[name] = function () {
			const keys_api = root.frappe && root.frappe.ui && root.frappe.ui.keys;
			if (!keys_api || typeof keys_api.add_shortcut !== "function") return original.apply(this, arguments);
			const add = keys_api.add_shortcut;
			keys_api.add_shortcut = (opts = {}) => (keys.includes(opts.shortcut) ? undefined : add(opts));
			try {
				return original.apply(this, arguments);
			} finally {
				keys_api.add_shortcut = add;
			}
		};
	}
	if (missing.length) console.warn(`nxt_theme: ${entry.app}'s script changed; not contained: ${missing.join(", ")}`);
	document.documentElement.dataset[`contained${entry.app.replace(/(^|_)(\w)/g, (m, a, c) => c.toUpperCase())}Js`] =
		missing.length ? `partial (${missing.length} not found)` : "yes";
	return missing;
}

export function contain_app_scripts(root = window) {
	if (typeof root.$ !== "function") return;
	root.$(document).ready(() => {
		for (const entry of CONTAIN) if (entry.script) contain_script(entry, root);
	});
}

function sheet_path(sheet) {
	try {
		return new URL(sheet.href, window.location.href).pathname;
	} catch (e) {
		return null;
	}
}

function delete_rule(sheet, { media, selector }) {
	const parents = media
		? [...sheet.cssRules].filter((r) => r instanceof CSSMediaRule && r.media.mediaText === media)
		: [sheet];
	for (const parent of parents) {
		const rules = parent.cssRules;
		for (let i = rules.length - 1; i >= 0; i--) {
			if (rules[i] instanceof CSSStyleRule && rules[i].selectorText === selector) {
				parent.deleteRule(i);
				return true;
			}
		}
	}
	return false;
}

function drop_property(sheet, { selector, property }) {
	for (const rule of sheet.cssRules) {
		if (rule instanceof CSSStyleRule && rule.selectorText === selector && rule.style.getPropertyValue(property)) {
			rule.style.removeProperty(property);
			return true;
		}
	}
	return false;
}

export function contain(sheet, entry) {
	const missing = [];
	for (const r of entry.rules) if (!delete_rule(sheet, r)) missing.push((r.media ? r.media + " " : "") + r.selector);
	for (const p of entry.properties) if (!drop_property(sheet, p)) missing.push(p.selector + " " + p.property);
	if (missing.length) {
		console.warn(`nxt_theme: ${entry.app}'s stylesheet changed; left in place: ${missing.join(", ")}`);
	}
	document.documentElement.dataset[`contained${entry.app.replace(/(^|_)(\w)/g, (m, a, c) => c.toUpperCase())}`] =
		missing.length ? `partial (${missing.length} not found)` : "yes";
	return missing;
}

export function contain_app_css() {
	for (const entry of CONTAIN) {
		for (const link of document.querySelectorAll('link[rel="stylesheet"]')) {
			if (!link.href || new URL(link.href, window.location.href).pathname !== entry.sheet) continue;
			const run = () => {
				if (link.dataset.nxtContained) return;
				link.dataset.nxtContained = "1";
				contain(link.sheet, entry);
			};
			// Normally loaded already; if not, contain it the moment it is.
			if (link.sheet && sheet_path(link.sheet) === entry.sheet) {
				try {
					link.sheet.cssRules;
					run();
					continue;
				} catch (e) {
					// not readable yet
				}
			}
			link.addEventListener("load", run, { once: true });
		}
	}
}

contain_app_css();
contain_app_scripts();
